'use client';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Div, Media, Modal, Text, TextField } from '@pezeshk-book/ui-kit';
import Image from '@elements/image';
import { CountDown } from '@pezeshk-book/modules';
import validMobileNumber from '@utils/helpers/phone-validation';
import { AuthActions } from '@store/auth/auth-actions';
import { RootState } from '@store/root-reducer';
import { useDebouncedCallback } from 'use-debounce';
import Chip from '@pezeshk-book/ui-kit/chip';
import Link from 'next/link';
import routes from '@routes';
import useCaptcha from '@hooks/use-captcha';

const LoginModal = () => {
  const { confirmLoading, validationCodeLifetime, sendVerificationLoading, isNumberValid, verifyCode, countDownOver, countDownReset, loginModal, isAlreadyGetTheCodeFlag, isRetryingToGetNewCode } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if ('OTPCredential' in window) {
      const ac = new AbortController();

      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        } as CredentialRequestOptions & { otp: { transport: string[] } })
        .then((otpCredential: any) => {
          dispatch(AuthActions.checkSmsCodeAndLogin({ verifyCode: otpCredential.code }));
          ac.abort();
        })
        .catch(() => {
          ac.abort();
          // console.log(err);
        });
    }
  });

  useEffect(() => {
    if (!loginModal) {
      dispatch(AuthActions.setCountDownOver());
    }
  }, [loginModal]);

  // useEffect(() => {
  //   if (verifyCode.length === 4) {
  //     checkVerifyCode()
  //   }
  // }, [verifyCode]);

  const counterCallbackFunction = () => {
    dispatch(AuthActions.setCountDownOver());
  };

  const mobileValidation = useDebouncedCallback((e) => {
    const value = e.target.value;
    if (value.length >= 10) {
      const validMobile = validMobileNumber(value);
      if (validMobile.isValid) {
        dispatch(AuthActions.setClientMobile({ phoneNumber: `0${validMobile.mobile}` }));
      } else {
        dispatch(AuthActions.setMobileValidation({ isNumberValid: false }));
      }
    } else if (isNumberValid) {
      dispatch(AuthActions.setMobileValidation({ isNumberValid: false }));
    }
  }, 500);

  const clientVerificationCode = ({ type, isRetryingToGetNewCode, token }: { type: string, isRetryingToGetNewCode: boolean, token: string }) => {
    dispatch(AuthActions.sendVerificationCode({ type: type, isRetryingToGetNewCode: isRetryingToGetNewCode, token: token }));
  };

  const getCaptchaToken = (token: string) => {
    clientVerificationCode({ type: 'sms', isRetryingToGetNewCode: false, token: token });
  };

  const { handleReCaptchaVerify } = useCaptcha(getCaptchaToken);

  const codeInputHandler = (e: any) => {
    dispatch(AuthActions.setValidationCode({ verifyCode: e?.target?.value }));
  };

  const onClose = () => {
    dispatch(AuthActions.setLoginModal({ loginModal: false }));
  };

  const getCodeHandler = () => {
    if (codeRef?.current) {
      codeRef?.current?.focus();
    }
    handleReCaptchaVerify();
  };

  return (
    <Modal open={loginModal} onClose={onClose}>
      <Div className={'w-11/12 md:w-7/12 flex-col'}>
        <Div className={'flex-col bg-white pt-2 pl-2 rounded-2xl'}>
          <Chip value={'بستن'} variant={'outlined'} color={'danger'} className={'self-end'} onClick={onClose} onDelete={onClose} />
          <Div className={'flex-col md:flex-row relative justify-between p-5 md:p-10'}>
            <Div className={'w-full md:w-1/2 flex-col items-start'}>
              <Text typography={'xl'}>ثبت نام / ورود</Text>
              <Div className={'z-10 flex-col items-end pt-16 w-full gap-10'}>
                <TextField
                  size={'large'}
                  inputMode={'numeric'}
                  onChange={mobileValidation}
                  placeholder={'شماره موبایل خود را وارد کنید'}
                  placeholderPosition={'center'}
                  className={'w-full'}
                  inputClassName={'!text-center sm:!text-d-lg tracking-[10px] sm:tracking-[20px] placeholder-shown:tracking-normal'}
                />
                <Div className={'flex-col w-full gap-2'}>
                  <Div>
                    <TextField
                      value={verifyCode}
                      ref={codeRef}
                      type={'text'}
                      autoComplete={'one-time-code'}
                      size={'large'}
                      disabled={!isNumberValid}
                      inputMode={'numeric'}
                      maxLength={4}
                      onChange={codeInputHandler}
                      placeholder={'____'}
                      placeholderPosition={'center'}
                      className={'w-full'}
                      inputClassName={'content-center !text-center sm:!text-d-lg tracking-[20px]'}
                    />
                  </Div>
                  <Text
                    align={'right'}
                    color={'grey.500'}
                    typography={'sm'}>
                  کد اعتبار سنجی به شماره همراه شما پیامک خواهد شد
                  </Text>
                </Div>
              </Div>
              <Div className={'w-full mt-10 gap-x-5'}>
                {(!countDownOver && !isAlreadyGetTheCodeFlag) || confirmLoading || (!countDownOver && isAlreadyGetTheCodeFlag) || isRetryingToGetNewCode ? (
                  <Button
                    loading={sendVerificationLoading || confirmLoading}
                    size={'large'}
                    disabled={!isNumberValid || confirmLoading || sendVerificationLoading}
                    onClick={getCodeHandler}
                    className={'w-full whitespace-nowrap'}>
                  دریافت کد
                  </Button>
                ) : (
                  <Div className={'min-h-12 md:min-h-14 w-full flex-col'}>
                    <CountDown
                      variant={'contained'}
                      parentCallback={counterCallbackFunction}
                      sec={validationCodeLifetime}
                      reset={countDownReset}
                    />
                  </Div>
                )}
              </Div>
              <Div className={'w-full md:w-3/4 mt-4'}>
                <Text align={'right'} typography={'xs'}>ورود شما به معنای پذیرش <Link href={routes['route.policy.index']} className={'text-tertiary'}>قوانین حریم ‌خصوصی</Link> است</Text>
              </Div>
            </Div>
            <Media greaterThan={'xs'} className={'w-full md:w-5/12 justify-center'}>
              <Div className={'self-center w-full h-full relative'}>
                <Image
                  fill={true}
                  className={'w-96 h-96 object-contain'}
                  alt={'Register'}
                  src={'/images/home-screen/background-svg/phone.png'}
                />
              </Div>
            </Media>
          </Div>
        </Div>
      </Div>
    </Modal>
  );
};

export default LoginModal;
